<template>
  <div class="download">
    <div class="icon-container">
      <svg class="download-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" fill="#3591d8"/>
        <rect x="7" y="7" width="10" height="2" fill="white"/>
        <rect x="7" y="10" width="8" height="2" fill="white"/>
        <rect x="7" y="13" width="6" height="2" fill="white"/>
      </svg>
    </div>
    <button 
      @click="generatePdf" 
      class="download-btn"
      :disabled="isGenerating"
    >
      <div v-if="isGenerating" class="btn-text">
        <span>Generando PDF...</span>
      </div>
      <div v-else class="btn-text">
        <b><span>Descargar horario y cotización</span></b>
      </div>
    </button>
  </div>
</template>

<script>
import courseColorsStore from '@/store/courseColorsStore';
import { formatFullCourseName } from '@/utils/subjectNameUtils';

export default {
  name: 'PdfGenerator',
  props: {
    allData: { type: Object, required: true },
    quote: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      isGenerating: false
    }
  },
  
  mounted() {
  },

  methods: {
    async generatePdf() {
      this.isGenerating = true;
      
      try {
        // Crear el HTML del PDF desde cero
        const pdfHTML = this.createPdfHTML();
        
        // Crear un elemento temporal para el PDF
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = pdfHTML;
        document.body.appendChild(tempContainer);
        
        // Configurar opciones del PDF
        const options = {
          margin: [20, 15, 30, 15],
          filename: 'cotizacion-pdv.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true
          },
          jsPDF: { 
            unit: "mm", 
            format: "a4", 
            orientation: "portrait" 
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        // Carga dinámica para evitar traer fast-png/jspdf en el arranque
        const { default: html2pdf } = await import(/* webpackChunkName: "html2pdf" */ 'html2pdf.js');
        await html2pdf().set(options).from(tempContainer.firstElementChild).save();
        
        // Limpiar el elemento temporal
        document.body.removeChild(tempContainer);
        
      } catch (error) {
        console.error('Error generando PDF:', error);
      } finally {
        this.isGenerating = false;
      }
    },

    createPdfHTML() {
      // Función para limpiar el nombre de la sede
      const cleanLocationName = (locationName) => {
        if (!locationName) return "";
        let cleanName = locationName;
        cleanName = cleanName.replace(/Preuniversitario\s*/gi, '');
        cleanName = cleanName.replace(/Pre Sede\s*/gi, '');
        return cleanName.trim();
      };

      // Obtener datos del estudiante
      const studentInfo = {
        program: this.allData.step1?.program?.name || "",
        name: `${this.allData.step2?.firstName || ""} ${
          this.allData.step2?.firstLastName || ""
        } ${this.allData.step2?.secondLastName || ""}`.trim(),
        location: cleanLocationName(this.allData.step2?.preferredLocation?.name) || "",
      };

      // Obtener cursos seleccionados
      const selectedCourses = this.allData.step3?.selectedCourses || [];
      
      // Calcular pricing
      const pricing = this.calculatePricing();
      


      return `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; background: white;">
          <!-- Header -->
          <div style="background: #FFD700; padding: 8px; text-align: center; margin-bottom: 20px; border-radius: 4px;">
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
              viewBox="0 0 190 76" style="enable-background:new 0 0 190 76; width: 140px; height: 56px;" xml:space="preserve">
              <style type="text/css">
                .st0{fill:#020203;}
                .st1{fill:#FFD100;}
              </style>
              <g>
                <g>
                  <g>
                    <rect x="1" y="53.82" class="st0" width="188" height="2.82"/>
                    <rect x="1" y="29.82" class="st0" width="188" height="22.57"/>
                    <g>
                      <path class="st1" d="M4.55,35.62H8.3c0.61,0,1.17,0.07,1.7,0.21c0.53,0.14,0.98,0.35,1.37,0.62c0.39,0.28,0.7,0.62,0.92,1.04
                          c0.22,0.42,0.34,0.9,0.34,1.45c0,0.56-0.11,1.04-0.33,1.46c-0.22,0.42-0.51,0.76-0.87,1.04c-0.36,0.28-0.77,0.48-1.24,0.62
                          c-0.46,0.14-0.94,0.21-1.42,0.21H7.2v5.15H4.55V35.62z M7.2,41.19h0.86c0.3,0,0.56-0.06,0.78-0.18
                          c0.22-0.12,0.41-0.28,0.56-0.49c0.15-0.21,0.26-0.44,0.33-0.71c0.07-0.27,0.11-0.55,0.11-0.84c0-0.35-0.05-0.67-0.14-0.94
                          c-0.1-0.28-0.23-0.51-0.4-0.7c-0.17-0.19-0.37-0.33-0.59-0.43c-0.23-0.1-0.47-0.15-0.73-0.15H7.2V41.19z"/>
                      <polygon class="st1" points="23.76,35.62 23.76,36.62 18.36,36.62 18.36,40.51 22.84,40.51 22.84,41.52 18.36,41.52 18.36,46.2 
                      23.76,46.2 23.76,47.41 15.72,47.41 15.72,35.62 				"/>
                      <path class="st1" d="M26.84,35.62h3.91c1,0,1.93,0.13,2.77,0.38c0.84,0.26,1.57,0.63,2.17,1.13c0.61,0.49,1.08,1.1,1.42,1.82
                          c0.34,0.72,0.51,1.54,0.51,2.46c0,0.83-0.14,1.61-0.43,2.34c-0.29,0.73-0.7,1.37-1.25,1.91c-0.55,0.54-1.22,0.97-2.02,1.28
                          c-0.8,0.31-1.71,0.47-2.75,0.47h-4.32V35.62z M29.47,46.2h1.41c0.52,0,1.01-0.1,1.47-0.31c0.46-0.21,0.86-0.52,1.2-0.92
                          c0.34-0.41,0.61-0.9,0.81-1.49c0.2-0.59,0.3-1.27,0.3-2.04c0-0.66-0.09-1.28-0.28-1.84c-0.19-0.56-0.45-1.05-0.78-1.47
                          c-0.33-0.41-0.73-0.74-1.2-0.97c-0.46-0.23-0.97-0.35-1.52-0.35h-1.41V46.2z"/>
                      <path class="st1" d="M40.14,35.62h4.05c0.69,0,1.3,0.08,1.83,0.24c0.52,0.16,0.96,0.38,1.31,0.67c0.35,0.29,0.61,0.63,0.78,1.02
                          c0.17,0.39,0.26,0.82,0.26,1.28c0,0.33-0.06,0.65-0.18,0.95c-0.12,0.3-0.29,0.58-0.5,0.83c-0.22,0.25-0.48,0.47-0.78,0.67
                          c-0.3,0.19-0.64,0.36-1,0.48l3.83,5.65h-2.87l-3.62-5.34h-0.47v5.34h-2.64V35.62z M42.78,41.05h0.58c0.32,0,0.62-0.05,0.9-0.15
                          c0.28-0.1,0.51-0.24,0.72-0.43c0.2-0.19,0.36-0.41,0.48-0.68c0.12-0.27,0.17-0.57,0.17-0.91c0-0.31-0.04-0.59-0.13-0.85
                          c-0.09-0.26-0.21-0.48-0.37-0.67c-0.16-0.19-0.34-0.34-0.56-0.44c-0.21-0.1-0.45-0.16-0.71-0.16h-1.07V41.05z"/>
                      <path class="st1" d="M57.28,47.67c-0.58,0-1.13-0.07-1.66-0.21c-0.53-0.14-1.02-0.34-1.47-0.6c-0.45-0.26-0.87-0.57-1.24-0.94
                          c-0.37-0.37-0.69-0.79-0.95-1.25c-0.26-0.46-0.46-0.97-0.61-1.51c-0.14-0.54-0.22-1.12-0.22-1.73c0-0.6,0.07-1.16,0.22-1.69
                          c0.14-0.53,0.35-1.02,0.61-1.47c0.26-0.45,0.58-0.85,0.95-1.21c0.37-0.36,0.79-0.67,1.24-0.92c0.45-0.25,0.94-0.44,1.47-0.57
                          c0.53-0.14,1.08-0.2,1.66-0.2c0.58,0,1.13,0.07,1.66,0.2c0.53,0.14,1.02,0.33,1.47,0.58c0.45,0.25,0.87,0.56,1.24,0.92
                          c0.37,0.36,0.69,0.76,0.95,1.21c0.26,0.45,0.47,0.94,0.61,1.46c0.14,0.53,0.22,1.09,0.22,1.68c0,0.62-0.07,1.21-0.22,1.76
                          c-0.14,0.55-0.35,1.06-0.61,1.52c-0.26,0.46-0.58,0.88-0.95,1.25c-0.37,0.37-0.79,0.68-1.24,0.94
                          c-0.45,0.26-0.95,0.45-1.47,0.59C58.41,47.61,57.86,47.67,57.28,47.67 M57.28,46.67c0.52,0,0.99-0.13,1.39-0.39
                          c0.4-0.26,0.74-0.63,1.01-1.09c0.27-0.47,0.48-1.02,0.62-1.66c0.14-0.64,0.21-1.34,0.21-2.09c0-0.75-0.07-1.43-0.21-2.05
                          c-0.14-0.62-0.34-1.15-0.62-1.6c-0.27-0.45-0.61-0.8-1.01-1.04c-0.4-0.25-0.86-0.37-1.39-0.37c-0.53,0-1,0.12-1.4,0.37
                          c-0.4,0.25-0.74,0.6-1.01,1.04c-0.27,0.45-0.47,0.98-0.61,1.6c-0.14,0.62-0.21,1.31-0.21,2.05c0,0.76,0.07,1.45,0.21,2.09
                          c0.14,0.64,0.34,1.19,0.61,1.66c0.27,0.47,0.61,0.83,1.01,1.09C56.28,46.54,56.75,46.67,57.28,46.67"/>
                      <path class="st1" d="M71.8,35.62h3.91c1,0,1.92,0.13,2.77,0.38c0.84,0.26,1.57,0.63,2.17,1.13c0.61,0.49,1.08,1.1,1.42,1.82
                          c0.34,0.72,0.51,1.54,0.51,2.46c0,0.83-0.14,1.61-0.43,2.34c-0.28,0.73-0.7,1.37-1.25,1.91c-0.55,0.54-1.22,0.97-2.02,1.28
                          c-0.8,0.31-1.71,0.47-2.75,0.47H71.8V35.62z M74.44,46.2h1.41c0.52,0,1.01-0.1,1.47-0.31c0.46-0.21,0.86-0.52,1.2-0.92
                          c0.34-0.41,0.61-0.9,0.81-1.49c0.2-0.59,0.3-1.27,0.3-2.04c0-0.66-0.09-1.28-0.28-1.84c-0.19-0.56-0.45-1.05-0.78-1.47
                          c-0.34-0.41-0.74-0.74-1.2-0.97c-0.46-0.23-0.97-0.35-1.52-0.35h-1.41V46.2z"/>
                      <polygon class="st1" points="92.8,35.62 92.8,36.62 87.41,36.62 87.41,40.51 91.89,40.51 91.89,41.52 87.41,41.52 87.41,46.2 
                      92.8,46.2 92.8,47.41 84.77,47.41 84.77,35.62 				"/>
                      <polygon class="st1" points="103.35,35.62 106.91,43.77 110.21,35.62 111.91,35.62 107.06,47.59 105.82,47.59 100.62,35.62 				
                          	"/>
                      <path class="st1" d="M118.13,35.36l5.04,12.05h-2.7l-1.34-3.22h-4.18l-1.41,3.22h-1.8l5.25-12.05H118.13z M115.44,43.09h3.24
                          l-1.6-3.81L115.44,43.09z"/>
                      <polygon class="st1" points="125.57,35.62 128.21,35.62 128.21,46.2 133.7,46.2 133.7,47.41 125.57,47.41 				"/>
                      <path class="st1" d="M136.25,35.62h3.91c1,0,1.92,0.13,2.77,0.38c0.84,0.26,1.57,0.63,2.17,1.13c0.61,0.49,1.08,1.1,1.42,1.82
                          c0.34,0.72,0.51,1.54,0.51,2.46c0,0.83-0.14,1.61-0.43,2.34c-0.28,0.73-0.7,1.37-1.25,1.91c-0.55,0.54-1.22,0.97-2.02,1.28
                          c-0.8,0.31-1.72,0.47-2.75,0.47h-4.32V35.62z M138.89,46.2h1.41c0.52,0,1.01-0.1,1.47-0.31c0.46-0.21,0.86-0.52,1.2-0.92
                          c0.34-0.41,0.61-0.9,0.81-1.49c0.2-0.59,0.3-1.27,0.3-2.04c0-0.66-0.09-1.28-0.28-1.84c-0.19-0.56-0.45-1.05-0.78-1.47
                          c-0.34-0.41-0.74-0.74-1.2-0.97c-0.46-0.23-0.97-0.35-1.52-0.35h-1.41V46.2z"/>
                      <rect x="150.13" y="35.62" class="st1" width="2.64" height="11.79"/>
                      <polygon class="st1" points="158.1,35.62 161.66,43.77 164.95,35.62 166.65,35.62 161.81,47.59 160.57,47.59 155.37,35.62 				
                          	"/>
                      <rect x="168.77" y="35.62" class="st1" width="2.64" height="11.79"/>
                      <path class="st1" d="M180.41,35.36l5.04,12.05h-2.7l-1.35-3.22h-4.18l-1.41,3.22h-1.8l5.25-12.05H180.41z M177.72,43.09h3.24
                          l-1.6-3.81L177.72,43.09z"/>
                    </g>
                    <g>
                      <path class="st0" d="M1.04,19.65c0-0.09,0.08-0.18,0.18-0.18h2.24c1.17,0,2.14,0.95,2.14,2.11c0,1.18-0.96,2.15-2.13,2.15H2.28
                          v2.24c0,0.1-0.09,0.18-0.18,0.18H1.22c-0.11,0-0.18-0.09-0.18-0.18V19.65z M3.38,22.55c0.53,0,0.98-0.43,0.98-0.99
                          c0-0.52-0.45-0.92-0.98-0.92H2.28v1.92H3.38z"/>
                      <path class="st0" d="M13.36,19.65c0-0.09,0.08-0.18,0.18-0.18h2.73c1.14,0,2.08,0.92,2.08,2.05c0,0.88-0.58,1.59-1.41,1.93
                          l1.31,2.42c0.07,0.12,0,0.28-0.16,0.28h-1c-0.09,0-0.13-0.05-0.15-0.09l-1.27-2.53H14.6v2.43c0,0.1-0.09,0.18-0.18,0.18h-0.88
                          c-0.1,0-0.18-0.09-0.18-0.18V19.65z M16.17,22.51c0.51,0,0.94-0.44,0.94-0.97c0-0.51-0.44-0.93-0.94-0.93h-1.55v1.9H16.17z"/>
                      <path class="st0" d="M26.4,19.65c0-0.09,0.08-0.18,0.18-0.18h3.88c0.1,0,0.18,0.09,0.18,0.18v0.78c0,0.09-0.08,0.18-0.18,0.18
                          h-2.82v1.56h2.36c0.1,0,0.18,0.09,0.18,0.18v0.79c0,0.1-0.09,0.18-0.18,0.18h-2.36V25h2.82c0.1,0,0.18,0.09,0.18,0.18v0.78
                          c0,0.1-0.08,0.18-0.18,0.18h-3.88c-0.11,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M38.59,19.65c0-0.09,0.09-0.18,0.18-0.18h0.91c0.1,0,0.18,0.09,0.18,0.18v3.9c0,0.81,0.55,1.45,1.38,1.45
                          c0.83,0,1.39-0.64,1.39-1.44v-3.91c0-0.09,0.08-0.18,0.18-0.18h0.91c0.1,0,0.18,0.09,0.18,0.18v3.97c0,1.45-1.15,2.62-2.66,2.62
                          c-1.5,0-2.65-1.17-2.65-2.62V19.65z"/>
                      <path class="st0" d="M52.22,19.54c0-0.1,0.09-0.17,0.18-0.17h0.24l3.97,4.22h0.01v-3.94c0-0.09,0.08-0.18,0.18-0.18h0.88
                          c0.1,0,0.18,0.09,0.18,0.18v6.42c0,0.1-0.09,0.17-0.18,0.17h-0.23l-3.99-4.38h-0.01v4.1c0,0.1-0.08,0.18-0.18,0.18H52.4
                          c-0.1,0-0.18-0.09-0.18-0.18V19.54z"/>
                      <path class="st0" d="M66.28,19.65c0-0.09,0.09-0.18,0.18-0.18h0.89c0.09,0,0.18,0.09,0.18,0.18v6.32c0,0.1-0.09,0.18-0.18,0.18
                          h-0.89c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M75.08,19.71c-0.06-0.12,0.02-0.25,0.16-0.25h0.97c0.09,0,0.14,0.06,0.16,0.1l1.83,4.1h0.07l1.83-4.1
                          c0.02-0.05,0.08-0.1,0.16-0.1h0.97c0.14,0,0.22,0.12,0.16,0.25l-2.96,6.42c-0.03,0.06-0.08,0.1-0.16,0.1h-0.1
                          c-0.09,0-0.13-0.05-0.16-0.1L75.08,19.71z"/>
                      <path class="st0" d="M88.95,19.65c0-0.09,0.08-0.18,0.18-0.18h3.88c0.11,0,0.18,0.09,0.18,0.18v0.78c0,0.09-0.08,0.18-0.18,0.18
                          h-2.82v1.56h2.36c0.1,0,0.18,0.09,0.18,0.18v0.79c0,0.1-0.09,0.18-0.18,0.18h-2.36V25h2.82c0.11,0,0.18,0.09,0.18,0.18v0.78
                          c0,0.1-0.08,0.18-0.18,0.18h-3.88c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M101.24,19.65c0-0.09,0.08-0.18,0.18-0.18h2.73c1.14,0,2.08,0.92,2.08,2.05c0,0.88-0.58,1.59-1.41,1.93
                          l1.31,2.42c0.07,0.12,0,0.28-0.16,0.28h-1c-0.09,0-0.13-0.05-0.15-0.09l-1.27-2.53h-1.06v2.43c0,0.1-0.09,0.18-0.18,0.18h-0.88
                          c-0.1,0-0.18-0.09-0.18-0.18V19.65z M104.04,22.51c0.51,0,0.94-0.44,0.94-0.97c0-0.51-0.44-0.93-0.94-0.93h-1.55v1.9H104.04z"/>
                      <path class="st0" d="M113.88,25.21l0.34-0.59c0.08-0.13,0.24-0.13,0.32-0.07c0.05,0.03,0.82,0.59,1.44,0.59
                          c0.49,0,0.87-0.33,0.87-0.73c0-0.49-0.41-0.82-1.21-1.15c-0.9-0.36-1.79-0.94-1.79-2.06c0-0.85,0.63-1.83,2.15-1.83
                          c0.97,0,1.72,0.5,1.91,0.64c0.1,0.06,0.12,0.22,0.06,0.31l-0.36,0.54c-0.08,0.11-0.22,0.19-0.33,0.11
                          c-0.08-0.05-0.8-0.52-1.32-0.52c-0.55,0-0.84,0.36-0.84,0.67c0,0.45,0.35,0.75,1.13,1.07c0.92,0.37,1.99,0.93,1.99,2.16
                          c0,0.98-0.85,1.89-2.19,1.89c-1.2,0-1.91-0.56-2.1-0.74C113.84,25.41,113.79,25.36,113.88,25.21"/>
                      <path class="st0" d="M126.12,19.65c0-0.09,0.09-0.18,0.18-0.18h0.89c0.09,0,0.18,0.09,0.18,0.18v6.32c0,0.1-0.09,0.18-0.18,0.18
                          h-0.89c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M136.68,20.61h-1.46c-0.1,0-0.18-0.09-0.18-0.18v-0.78c0-0.09,0.08-0.18,0.18-0.18h4.17
                          c0.1,0,0.18,0.09,0.18,0.18v0.78c0,0.09-0.08,0.18-0.18,0.18h-1.46v5.35c0,0.1-0.09,0.18-0.18,0.18h-0.89
                          c-0.1,0-0.18-0.09-0.18-0.18V20.61z"/>
                      <path class="st0" d="M146.01,25.89l2.97-6.42c0.03-0.06,0.08-0.1,0.16-0.1h0.1c0.1,0,0.13,0.05,0.16,0.1l2.94,6.42
                          c0.06,0.12-0.02,0.25-0.16,0.25h-0.83c-0.14,0-0.21-0.06-0.28-0.19l-0.47-1.03h-2.85l-0.47,1.03c-0.04,0.1-0.12,0.19-0.28,0.19
                          h-0.83C146.03,26.14,145.96,26.02,146.01,25.89 M150.13,23.86l-0.95-2.1h-0.03l-0.93,2.1H150.13z"/>
                      <path class="st0" d="M159.88,19.65c0-0.09,0.08-0.18,0.18-0.18h2.73c1.14,0,2.08,0.92,2.08,2.05c0,0.88-0.58,1.59-1.41,1.93
                          l1.31,2.42c0.07,0.12,0,0.28-0.16,0.28h-1c-0.09,0-0.13-0.05-0.15-0.09l-1.27-2.53h-1.06v2.43c0,0.1-0.08,0.18-0.18,0.18h-0.88
                          c-0.1,0-0.18-0.09-0.18-0.18V19.65z M162.69,22.51c0.51,0,0.94-0.44,0.94-0.97c0-0.51-0.44-0.93-0.94-0.93h-1.55v1.9H162.69z"/>
                      <path class="st0" d="M172.92,19.65c0-0.09,0.09-0.18,0.18-0.18h0.89c0.09,0,0.18,0.09,0.18,0.18v6.32c0,0.1-0.09,0.18-0.18,0.18
                          h-0.89c-0.1,0-0.18-0.09-0.18-0.18V19.65z"/>
                      <path class="st0" d="M185.53,19.37c1.91,0,3.43,1.54,3.43,3.44c0,1.91-1.53,3.42-3.43,3.42c-1.91,0-3.42-1.52-3.42-3.42
                          C182.11,20.9,183.62,19.37,185.53,19.37 M185.53,25c1.2,0,2.19-0.98,2.19-2.18c0-1.2-0.99-2.2-2.19-2.2c-1.2,0-2.18,1-2.18,2.2
                          C183.35,24.01,184.33,25,185.53,25"/>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>

          <!-- Main Content -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px;">
            
            <!-- Left Column: Information -->
            <div style="background: #f9f9f9; padding: 24px; border-radius: 8px;">
              <!-- Program Info -->
              <div style="display: flex; flex-direction: column; gap: 16px; margin-bottom: 24px;">
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="color: #666; font-size: 16px; width: 20px; text-align: center;">☰</span>
                  <div style="flex: 1;">
                    <span style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">Programa</span>
                    <div style="color: #333; font-weight: 600; font-size: 14px; margin-top: 2px;">${this.allData.step1?.program?.name || 'Programa no seleccionado'}</div>
                  </div>
                </div>
                
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="color: #666; font-size: 16px; width: 20px; text-align: center;">👤</span>
                  <div style="flex: 1;">
                    <span style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">Nombre del alumno</span>
                    <div style="color: #333; font-weight: 600; font-size: 14px; margin-top: 2px;">${studentInfo.name}</div>
                  </div>
                </div>
                
                <div style="display: flex; align-items: center; gap: 12px;">
                  <span style="color: #666; font-size: 16px; width: 20px; text-align: center;">📍</span>
                  <div style="flex: 1;">
                    <span style="color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;">Sede</span>
                    <div style="color: #333; font-weight: 600; font-size: 14px; margin-top: 2px;">${studentInfo.location}</div>
                  </div>
                </div>
              </div>

              <!-- Cursos -->
              <div style="margin-bottom: 24px;">
                <h3 style="font-size: 14px; font-weight: bold; color: #333; margin: 0 0 12px 0; text-transform: uppercase; letter-spacing: 0.5px;">Cursos</h3>
                <div style="display: flex; flex-direction: column; gap: 16px;">
                  ${this.getGroupedCourses(selectedCourses).map((course) => {
                    const color = this.getCourseColor(course.name);
                    const courseName = this.cleanCourseName(course.name);
                    return `
                      <div style="display: flex; flex-direction: column; gap: 8px;">
                        <div style="display: flex; align-items: center; gap: 12px;">
                          <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; flex-shrink: 0;"></div>
                          <div style="color: #333; font-weight: 600; font-size: 13px; line-height: 1.3;">${courseName}:</div>
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 4px; margin-left: 24px;">
                          ${course.schedules.map((schedule) => `
                            <div style="color: #666; font-size: 12px; line-height: 1.2;">${schedule}</div>
                          `).join('')}
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            </div>

            <!-- Right Column: Values and Pricing -->
            <div style="background: #f9f9f9; padding: 24px; border-radius: 8px;">
              <h2 style="font-size: 16px; font-weight: bold; color: #333; margin: 0 0 20px 0; text-transform: uppercase; letter-spacing: 0.5px;">Resumen de tu compra</h2>
              
              <!-- Price Breakdown -->
              <div style="display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px;">
                
                <div style="display: flex; justify-content: space-between; align-items: center; min-height: 20px; border-bottom: 1px solid #f0f0f0; padding: 4px 0;">
                  <span style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; flex: 1;">Total cursos</span>
                  <span style="color: #333; font-weight: 600; font-size: 13px; text-align: right; min-width: 80px;">$${this.formatPrice(pricing.precioSubTotal)}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; align-items: center; min-height: 20px; border-bottom: 1px solid #f0f0f0; padding: 4px 0;">
                  <span style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; flex: 1;">Desc. inscripción online (${pricing.porcentajeDescuento}%)</span>
                  <span style="color: #666; font-weight: 600; font-size: 13px; text-align: right; min-width: 80px;">-$${this.formatPrice(pricing.valorDescuento)}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; align-items: center; min-height: 20px; border-bottom: 1px solid #f0f0f0; padding: 4px 0;">
                  <span style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; flex: 1;">Matrícula online</span>
                  <span style="color: #666; font-weight: 600; font-size: 13px; text-align: right; min-width: 80px;">$${this.formatPrice(pricing.valorMatricula)}</span>
                </div>
                
                <div style="display: flex; justify-content: space-between; align-items: center; min-height: 20px; border-bottom: 1px solid #f0f0f0; padding: 4px 0;">
                  <span style="color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; flex: 1;">Total descuento</span>
                  <span style="color: #666; font-weight: 600; font-size: 13px; text-align: right; min-width: 80px;">$${this.formatPrice(pricing.valorDescuentoSesiones + pricing.valorDescuento)}</span>
                </div>
                
                <!-- Separador para cupón aplicado -->
                ${this.allData.step4?.coupon ? `
                <div style="border-top: 1px solid #e0e0e0; margin: 8px 0;"></div>
                
                <!-- Campos del cupón aplicado -->
                <div style="display: flex; justify-content: space-between; align-items: center; min-height: 20px; border-bottom: 1px solid #f0f0f0; padding: 4px 0;">
                  <span style="color: #10b981; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; flex: 1;">Cupón Aplicado</span>
                  <span style="color: #10b981; font-weight: 600; font-size: 13px; text-align: right; min-width: 80px;">
                    ${this.getCouponDiscountPercentage() === 100 ? '100%' : 'Cupón (' + this.getCouponDiscountPercentage() + '%)'}
                  </span>
                </div>
                
                <div style="display: flex; justify-content: space-between; align-items: center; min-height: 20px; border-bottom: 1px solid #f0f0f0; padding: 4px 0;">
                  <span style="color: #10b981; font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; flex: 1;">Descuento Cupón</span>
                  <span style="color: #10b981; font-weight: 600; font-size: 13px; text-align: right; min-width: 80px;">- $${this.formatPrice(this.getCouponDiscountAmount())}</span>
                </div>
                ` : ''}
              </div>

              <!-- Total to Pay -->
              <div style="display: flex; flex-direction: column; gap: 8px; border-top: 2px solid #e0e0e0; padding-top: 16px; align-items: flex-end;">
                <span style="color: #333; font-weight: bold; font-size: 16px; text-transform: uppercase; letter-spacing: 0.5px; align-self: flex-start;">Total a pagar</span>
                
                ${this.allData.step4?.coupon && this.allData.step4?.precioOriginalSinCupon ? `
                <div style="text-align: right;">
                  <div style="color: #666; font-size: 16px; text-decoration: line-through; margin-bottom: 4px;">$${this.formatPrice(this.allData.step4.precioOriginalSinCupon)}</div>
                  <div style="color: #333; font-weight: bold; font-size: 20px;">$${this.formatPrice(pricing.precioTotal)}</div>
                </div>
                ` : `
                <div style="text-align: right;">
                  <div style="color: #333; font-weight: bold; font-size: 20px;">$${this.formatPrice(pricing.precioTotal)}</div>
                </div>
                `}
                
                ${this.allData.step4?.coupon && this.allData.step4?.precioOriginalSinCupon ? `
                <!-- Tag de descuento aplicado -->
                <div style="display: flex; align-items: center; gap: 8px; background-color: #f0f8f0; border: 1px solid #10b981; border-radius: 20px; padding: 8px 16px; margin-top: 8px; width: fit-content;">
                  <div style="width: 16px; height: 16px; background-color: #10b981; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                    <span style="color: white; font-size: 10px; font-weight: bold;">✓</span>
                  </div>
                  <span style="font-size: 12px; font-weight: bold; color: #10b981; text-transform: uppercase; letter-spacing: 0.5px;">
                    ${this.getCouponDiscountPercentage() === 100 ? '100% DCTO.' : this.getCouponDiscountPercentage() + '% DCTO.'}
                  </span>
                </div>
                ` : ''}
              </div>
            </div>
          </div>

          <!-- Advertencias Informativas -->
          <div style="display: flex; flex-direction: column; gap: 16px; margin-bottom: 30px;">
            <!-- Primera Advertencia -->
            <div style="padding: 16px; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 6px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="color: #856404; font-size: 16px;">⚠️</span>
                <span style="color: #856404; font-weight: bold; font-size: 13px; text-transform: uppercase; letter-spacing: 0.3px;">Advertencia</span>
              </div>
              <p style="color: #856404; font-size: 12px; line-height: 1.4; margin: 0;">
                Este documento es únicamente informativo y no representa una compra real. Los precios y descuentos mostrados son referenciales y pueden variar. 
                Este PDF no tiene valor comercial ni legal, y solo sirve como confirmación de la cotización solicitada.
              </p>
            </div>

            <!-- Segunda Advertencia -->
            <div style="padding: 16px; background: #e3f2fd; border: 1px solid #90caf9; border-radius: 6px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="color: #1565c0; font-size: 16px;">ℹ️</span>
                <span style="color: #1565c0; font-weight: bold; font-size: 13px; text-transform: uppercase; letter-spacing: 0.3px;">Importante</span>
              </div>
              <p style="color: #1565c0; font-size: 12px; line-height: 1.4; margin: 0;">
                * La dirección de la sede "Avda. Apoquindo 5142, Las Condes" aparecerá por defecto al seleccionar programas Streaming.
              </p>
              <p style="color: #1565c0; font-size: 12px; line-height: 1.4; margin: 0;">
                ** El monto obtenido en tu cotización corresponde al valor anual de tu programa.
              </p>
            </div>
          </div>

          <!-- Calendar Section -->
          <div style="background: white; margin-bottom: 25px;">
            <h3 style="font-size: 18px; font-weight: bold; color: #333; margin: 0 0 15px 0; text-align: center;">Horario</h3>
            <div style="max-width: 800px; margin: 0 auto;">
              ${this.createPdfCalendarHTML()}
            </div>
          </div>

          <!-- Footer -->
          <div style="background: #f5f5f5; padding: 8px; text-align: center; border-top: 2px solid #FFD700; border-radius: 4px;">
            <div style="display: flex; flex-direction: column; align-items: center; gap: 4px;">
              <div style="font-size: 10px; color: #333; font-weight: 500;">
                Preuniversitario Pedro de Valdivia 2025
                <span style="color: #FF8C00; margin: 0 4px; font-size: 6px;">◆</span>
                https://preupdv.cl/
              </div>
              <div style="font-size: 9px; color: #666; font-weight: 400;">Si necesitas ayuda, llámanos</div>
              <div style="font-size: 11px; color: #333; font-weight: bold; display: flex; align-items: center; gap: 4px;">
                <span style="font-size: 9px;">📞</span>
                600 500 2020
              </div>
            </div>
          </div>
        </div>
      `;
    },



    createPdfCalendarHTML() {
      let selectedCourses = this.allData.step3?.selectedCourses || [];
      if (selectedCourses.length === 0) {
        selectedCourses = this.allData.step3?.selectedCoursesRaw || [];
      }
      
      const weekDays = [
        { short: 'LUN', full: 'LUN' },
        { short: 'MAR', full: 'MAR' },
        { short: 'MIE', full: 'MIE' },
        { short: 'JUE', full: 'JUE' },
        { short: 'VIE', full: 'VIE' },
        { short: 'SAB', full: 'SAB' }
      ];
      const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];
      
      let calendarHTML = `
        <div class="pdf-calendar" style="width: 100%; background: white; border-radius: 6px; border: 1px solid #e0e0e0; overflow: visible; max-width: 700px; margin: 0 auto; position: relative;">
          <div class="calendar-header" style="display: grid; grid-template-columns: 50px 105px 105px 105px 105px 105px 105px; background-color: #f8f9fa; border-bottom: 1px solid #e0e0e0;">
            <div class="time-column-header" style="padding: 8px 4px; font-weight: 600; color: #333; text-align: center; border-right: 1px solid #e0e0e0; font-size: 11px;">Hora</div>
      `;
      
      // Agregar encabezados de días
      weekDays.forEach(day => {
        calendarHTML += `
          <div class="day-header" style="padding: 8px 4px; font-weight: 600; color: #333; text-align: center; border-right: 1px solid #e0e0e0; font-size: 11px;">${day.short}</div>
        `;
      });
      
      calendarHTML += '</div><div class="calendar-body" style="display: grid; grid-template-columns: 50px 105px 105px 105px 105px 105px 105px; grid-template-rows: repeat(15, 35px); position: relative;">';
      
      // Crear filas para cada hora (solo celdas vacías)
      timeSlots.forEach(time => {
        calendarHTML += `
          <div class="time-row" style="display: contents;">
            <div class="time-label" style="padding: 4px; font-size: 9px; color: #666; text-align: center; border-right: 1px solid #e0e0e0; background-color: #f8f9fa; display: flex; align-items: center; justify-content: center; min-height: 35px;">${time}</div>
        `;
        
        // Agregar celdas vacías para cada día
        weekDays.forEach(() => {
          calendarHTML += `
            <div class="calendar-cell" style="border-right: 1px solid #e0e0e0; border-bottom: 1px solid #e0e0e0; min-height: 35px; position: relative; background-color: white; display: flex; align-items: center; justify-content: center; overflow: visible;"></div>
          `;
        });
        
        calendarHTML += '</div>';
      });
      
      // Agregar bloques de cursos como elementos absolutos sobre toda la cuadrícula
      const allCourses = this.getAllCoursesForCalendar(selectedCourses);
      allCourses.forEach(course => {
        
        const color = this.getCourseColor(course.name);
        const courseName = this.cleanCourseName(course.name);
        const timeRange = `${course.startTime}-${course.endTime}`;
        
        // Calcular posición basada en el día y tiempo
        const dayIndex = weekDays.findIndex(d => d.full === course.dayCode);
        const startHour = parseInt(course.startTime.split(':')[0]);
        const startMinute = parseInt(course.startTime.split(':')[1]);
        const endHour = parseInt(course.endTime.split(':')[0]);
        const endMinute = parseInt(course.endTime.split(':')[1]);
        
        // Convertir a minutos desde 08:00 (inicio del calendario)
        const startTotalMinutes = (startHour - 8) * 60 + startMinute;
        const endTotalMinutes = (endHour - 8) * 60 + endMinute;
        
        // Calcular posición y dimensiones absolutas usando anchos fijos
        const timeColumnWidth = 50; // ancho de la columna de tiempo
        const dayColumnWidth = 105; // ancho fijo de cada columna de día
        
        // Calcular posición con márgenes más precisos
        const marginLeft = 2; // margen izquierdo
        const marginRight = 2; // margen derecho
        const totalMargin = marginLeft + marginRight;
        
        const left = timeColumnWidth + (dayIndex * dayColumnWidth) + marginLeft + 'px';
        const top = (startTotalMinutes / 60) * 35 + 'px'; // 35px por hora
        const height = ((endTotalMinutes - startTotalMinutes) / 60) * 35 + 'px';
        const width = (dayColumnWidth - totalMargin) + 'px'; // Ancho exacto de la columna menos márgenes
        
        calendarHTML += `
          <div class="schedule-block" style="position: absolute; left: ${left}; top: ${top}; width: ${width}; height: ${height}; padding: 1px; color: white; font-size: 8px; font-weight: 500; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; border-radius: 1px; z-index: 10; box-sizing: border-box; background-color: ${color};">
            <div class="schedule-course" style="font-weight: 600; margin-bottom: 1px; line-height: 1.0; font-size: 7px;">${courseName}</div>
            <div class="schedule-time" style="font-size: 6px; opacity: 0.9; line-height: 1.0;">${timeRange}</div>
          </div>
        `;
      });
      
      calendarHTML += '</div></div>';
      return calendarHTML;
    },

    getFormattedCourseName(course) {
      // Buscar la materia (Subject) a la que pertenece el curso - MISMA LÓGICA QUE STEP 4
      const materia = this.findMateriaByCourseId(course.id || course.courseId);
      const originalSubjectName = materia?.subjectName || course.subjectName || course.name || '';
      
      // Detectar etiquetas basándose en el nombre del curso - MISMA LÓGICA QUE STEP 4
      const courseName = course.name || '';
      const hasMention = /MENCIÓN/gi.test(courseName) || /Mención/gi.test(courseName);
      const isElective = /ELECTIVO/gi.test(courseName) || /Electivo/gi.test(courseName);
      const isCommon = /COMÚN/gi.test(courseName) || /Común/gi.test(courseName);
      
      // Crear objeto curso con etiquetas basadas en el nombre - MISMA LÓGICA QUE STEP 4
      const courseWithLabels = {
        ...course,
        hasMention: hasMention,
        isElective: isElective,
        isCommon: isCommon
      };
      
      // Usar utilidad compartida para formatear nombre completo - MISMA LÓGICA QUE STEP 4
      return formatFullCourseName(originalSubjectName, courseWithLabels);
    },
    
    findMateriaByCourseId(courseId) {
      // Buscar en las materias del paso 3 para obtener la materia que contiene el curso
      const allMaterias = [
        ...(this.allData.step3?.leftColumnMaterias || []),
        ...(this.allData.step3?.rightColumnMaterias || [])
      ];
      
      for (const materia of allMaterias) {
        if (materia.courses) {
          const course = materia.courses.find(c => c.id == courseId);
          if (course) {
            return materia;
          }
        }
      }
      
      return null;
    },

    getGroupedCourses(selectedCourses) {
      const groupedCourses = {};
      
      selectedCourses.forEach(course => {
        // Aplicar el mismo formateo que se usa en Step 4
        const formattedName = this.getFormattedCourseName(course);
        
        if (!groupedCourses[formattedName]) {
          groupedCourses[formattedName] = {
            name: formattedName,
            schedules: []
          };
        }
        
        if (course.schedule) {
          // Formatear horario: "Viernes 19:00-20:20" -> "Viernes de 19:00 a 20:20"
          const [day, hour] = course.schedule.split(" ");
          const formattedSchedule = `${day} de ${hour.split('-')[0]} a ${hour.split('-')[1]}`;
          groupedCourses[formattedName].schedules.push(formattedSchedule);
        }
      });
      
      const result = Object.values(groupedCourses);
      return result;
    },

    getAllCoursesForCalendar(selectedCourses) {
      const processedCourses = [];
      
      // Mapeo de nombres de día a códigos
      const dayMapping = {
        'Lunes': 'LUN',
        'Martes': 'MAR', 
        'Miércoles': 'MIE',
        'Jueves': 'JUE',
        'Viernes': 'VIE',
        'Sábado': 'SAB'
      };
      
      selectedCourses.forEach(course => {
        
        if (course.schedule) {
          const parts = course.schedule.split(' ');
          
          if (parts.length >= 2) {
            const courseDay = parts[0];
            const timeRange = parts[1];
            
            // Encontrar el código de día correspondiente
            const dayCode = dayMapping[courseDay];
            
            if (dayCode) {
              const [startTime, endTime] = timeRange.split('-');
              
              // Aplicar formateo de nombre
              const formattedName = this.getFormattedCourseName(course);
              
              processedCourses.push({
                ...course,
                name: formattedName,
                dayCode: dayCode,
                startTime: startTime,
                endTime: endTime
              });
            }
          }
        }
      });
      return processedCourses;
    },

    processCoursesForCalendar(selectedCourses) {
      const processedCourses = [];
      
      // Mapeo de códigos de día
      const dayMapping = {
        'LUN': 'Lunes',
        'MAR': 'Martes', 
        'MIE': 'Miércoles',
        'MIÉ': 'Miércoles',
        'JUE': 'Jueves',
        'VIE': 'Viernes',
        'SAB': 'Sábado',
        'SÁB': 'Sábado'
      };
      
      selectedCourses.forEach(course => {
        
        if (course.schedule) {
          const parts = course.schedule.split(' ');
          
          if (parts.length >= 2) {
            const courseDay = parts[0];
            const timeRange = parts[1];
            
            // Encontrar el código de día correspondiente
            const dayCode = Object.keys(dayMapping).find(code => dayMapping[code] === courseDay);
            
            if (dayCode) {
              const [startTime, endTime] = timeRange.split('-');
              
              processedCourses.push({
                ...course,
                dayCode: dayCode,
                startTime: startTime,
                endTime: endTime
              });
            }
          }
        }
      });
      return processedCourses;
    },

    getCoursesForDayAndHour(day, hour) {
      const courses = [];
      
      // Mapeo inverso: de códigos de día a nombres completos
      const dayMapping = {
        'LUN': 'Lunes',
        'MAR': 'Martes', 
        'MIE': 'Miércoles',
        'MIÉ': 'Miércoles',
        'JUE': 'Jueves',
        'VIE': 'Viernes',
        'SAB': 'Sábado',
        'SÁB': 'Sábado'
      };
      
      const dayName = dayMapping[day];
      // Obtener cursos seleccionados
      const selectedCourses = this.allData.step3?.selectedCourses || [];
      
      selectedCourses.forEach(course => {
        
        if (course.schedule) {
          // Formato: "Sábado 12:35-13:55"
          const parts = course.schedule.split(' ');
          
          if (parts.length >= 2) {
            const courseDay = parts[0]; // "Sábado"
            const timeRange = parts[1]; // "12:35-13:55"
            
            // Verificar día
            if (courseDay === dayName) {
              
              // Extraer hora de inicio y fin
              const [startTime, endTime] = timeRange.split('-');
              const startHour = startTime.split(':')[0]; // "12"
              const startMinute = startTime.split(':')[1] || '00'; // "35"
              const endHour = endTime.split(':')[0]; // "13"
              const endMinute = endTime.split(':')[1] || '00'; // "55"
              
              // Convertir a minutos para comparación
              const startTotalMinutes = parseInt(startHour) * 60 + parseInt(startMinute);
              const endTotalMinutes = parseInt(endHour) * 60 + parseInt(endMinute);
              const currentHourMinutes = parseInt(hour.split(':')[0]) * 60;
              const nextHourMinutes = currentHourMinutes + 60;
              // Verificar si el curso se superpone con esta hora
              if (startTotalMinutes < nextHourMinutes && endTotalMinutes > currentHourMinutes) {
                courses.push({
                  ...course,
                  startMinutes: startTotalMinutes,
                  endMinutes: endTotalMinutes,
                  startTime: startTime,
                  endTime: endTime
                });
              }
            }
          }
        }
      });
      return courses;
    },

    getCourseColor(courseName) {
      // Usar el mismo sistema de colores que el paso 3 y 4
      const courseId = this.getCourseIdByName(courseName);
      const subjectName = this.getSubjectNameByCourseId(courseId);
      return courseColorsStore.getCourseColor(courseId, subjectName);
    },

    getCourseIdByName(courseName) {
      // Buscar el ID del curso por nombre en los cursos seleccionados
      const selectedCourses = this.allData.step3?.selectedCourses || [];
      const course = selectedCourses.find(c => c.name === courseName);
      return course?.id || course?.courseId || courseName;
    },

    getSubjectNameByCourseId(courseId) {
      // Buscar en las materias del paso 3 para obtener el subjectName correcto
      const allMaterias = [
        ...(this.allData.step3?.leftColumnMaterias || []),
        ...(this.allData.step3?.rightColumnMaterias || [])
      ];
      
      for (const materia of allMaterias) {
        if (materia.courses) {
          const course = materia.courses.find(c => c.id == courseId);
          if (course) {
            return materia.subjectName;
          }
        }
      }
      
      // Fallback: buscar en los cursos seleccionados
      const selectedCourse = this.allData.step3?.selectedCourses?.find(c => 
        (c.id || c.courseId) == courseId
      );
      
      return selectedCourse?.subjectName || selectedCourse?.name || '';
    },

    cleanCourseName(fullName) {
      if (!fullName) return '';
      let cleanName = fullName
        .replace(/\s*GE\s+(Tarde|Mañana)\s*\d*$/i, '')
        .replace(/\s*Tarde\s*\d*$/i, '')
        .replace(/\s*Mañana\s*\d*$/i, '')
        .replace(/\s*\d{3,4}$/, '')
        .trim();
      return cleanName || fullName;
    },

    calculatePricing() {
      const precioBruto = this.quote?.precioBruto || 0;
      const valorDescuentoSesiones = this.quote?.valorDescuentoSesiones || 0;
      const precioSubTotal = this.quote?.precioSubTotal || 0;
      const valorDescuento = this.quote?.valorDescuento || 0;
      const valorMatricula = this.quote?.valorMatricula || 0;
      const valorCupon = this.quote?.valorCupon || 0;
      const precioTotal = this.quote?.precioTotal || 0;

      // Calcular porcentajes
      const porcentajeDescuentoSesiones = precioBruto > 0 ? Math.round((valorDescuentoSesiones / precioBruto) * 100) : 0;
      const porcentajeDescuento = precioSubTotal > 0 ? Math.round((valorDescuento / precioSubTotal) * 100) : 0;
      
      // Calcular total de descuentos
      const totalDescuento = valorDescuentoSesiones + valorDescuento + valorCupon;
      
      // Calcular porcentaje total de descuento (EXCLUYENDO cupones)
      // Solo incluye descuentos por sesiones y matrícula online
      const porcentajeTotalDescuento = (() => {
        // Calcular el total de descuentos (sin incluir cupones)
        const totalDescuentosSinCupon = valorDescuentoSesiones + valorDescuento;
        
        // Calcular el porcentaje total como: (total descuentos / precio bruto) * 100
        const porcentaje = precioBruto > 0 ? Math.round((totalDescuentosSinCupon / precioBruto) * 100) : 0;
        
        return porcentaje;
      })();

      return {
        precioBruto,
        valorDescuentoSesiones,
        precioSubTotal,
        valorDescuento,
        valorMatricula,
        valorCupon,
        precioTotal,
        porcentajeDescuentoSesiones,
        porcentajeDescuento,
        totalDescuento,
        porcentajeTotalDescuento,
      };
    },

    getCouponDiscountPercentage() {
      // Detectar específicamente cupones del 100% - PRIORIDAD MÁXIMA
      const appliedCoupon = this.allData.step4?.coupon;
      if (appliedCoupon && appliedCoupon.toUpperCase().includes('100')) {
        return 100;
      }
      
      if (this.allData.step4?.precioOriginalSinCupon && this.quote?.precioTotal) {
        const descuento = this.allData.step4.precioOriginalSinCupon - this.quote.precioTotal;
        
        // Si el precio total es 0, es un cupón del 100%
        if (this.quote.precioTotal === 0) {
          return 100;
        }
        
        // Si el descuento es igual al precio original, es 100%
        if (this.allData.step4.precioOriginalSinCupon > 0 && descuento >= this.allData.step4.precioOriginalSinCupon) {
          return 100;
        }
        
        // Calcular el subtotal después del primer descuento (matrícula online)
        const precioSubTotal = this.quote?.precioSubTotal || 0;
        const valorDescuento = this.quote?.valorDescuento || 0;
        const subtotalDespuesPrimerDescuento = precioSubTotal - valorDescuento;
        
        // El porcentaje del cupón debe calcularse sobre el subtotal restante
        return subtotalDespuesPrimerDescuento > 0 ? 
          Math.round((descuento / subtotalDespuesPrimerDescuento) * 100) : 0;
      }
      return 0;
    },

    getCouponDiscountAmount() {
      if (this.allData.step4?.precioOriginalSinCupon && this.quote?.precioTotal) {
        return this.allData.step4.precioOriginalSinCupon - this.quote.precioTotal;
      }
      return 0;
    },

    formatPrice(price) {
      return price.toLocaleString("es-CL");
    }
  }
}
</script>

<style scoped>
.download {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding-top: 30px;
  width: 100%;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.download-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.download-btn {
  border: none;
  background: none;
  color: #3591d8;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.3s ease;
  font-family: Arial, sans-serif;
  text-align: left;
  padding: 0;
  line-height: 1.2;
  flex: 1;
  width: 100%;
}

.btn-text {
  display: flex;
  flex-direction: column;
  gap: 0;
  line-height: 1.2;
}

.btn-text span {
  display: block;
  line-height: 1.2;
}

.download-btn:hover {
  color: #2d7bb8;
}

.download-btn:disabled {
  color: #6c757d;
  cursor: not-allowed;
}
</style>
